/**
 * Quran ayah numbering helpers.
 *
 * The AlQuran Cloud / Islamic Network audio CDN uses a single global
 * ayah number from 1..6236 to address audio files, not (surah, ayah)
 * tuples. These helpers convert between the two and emit the audio
 * URL used by `useAyahAudio`.
 *
 * Pure functions, no React, no I/O. Safe to call from anywhere.
 */

import type { Surah } from '@/types/domain';

/**
 * Convert a (surahNumber, ayahNumber) pair into the canonical
 * global Quran ayah number (1..6236).
 *
 *   getGlobalAyahNumber(1, 1, surahs)  → 1
 *   getGlobalAyahNumber(1, 7, surahs)  → 7
 *   getGlobalAyahNumber(2, 1, surahs)  → 8
 *   getGlobalAyahNumber(2, 255, surahs) → 262
 *
 * `surahs` only needs `id` and `ayah_count`; we accept the wider
 * `Surah` row for caller convenience but read just those two fields.
 *
 * Defensive fallback: if `surahs` is empty or missing the target
 * surah's predecessors (loading state), we return `ayahNumber`
 * unchanged. Consumers should gate audio rendering on the surah
 * list being loaded so this fallback only fires during a brief
 * window.
 */
export function getGlobalAyahNumber(
  surahNumber: number,
  ayahNumber: number,
  surahs: Pick<Surah, 'id' | 'ayah_count'>[],
): number {
  if (!surahs || surahs.length === 0) return ayahNumber;
  const cumulative = surahs
    .filter((s) => s.id < surahNumber)
    .reduce((acc, s) => acc + (s.ayah_count ?? 0), 0);
  return cumulative + ayahNumber;
}

/**
 * Build the Mishary Rashid Alafasy audio URL for a global ayah number.
 *
 * Source: AlQuran Cloud edition `ar.alafasy`, served from the Islamic
 * Network CDN at 128 kbps. Public, CORS-enabled, no auth required.
 */
export function getMisharyAudioUrl(globalAyahNumber: number): string {
  return `https://cdn.islamic.network/quran/audio/128/ar.alafasy/${globalAyahNumber}.mp3`;
}
